import { useContext, useState } from "react";
import { AuthContext } from "context/context";
import { EndosoContext } from "pages/endoso/context";
import { endoso } from "services";
import {
  MENSAJE_REFACTURACION_CANAL_TRADICIONAL,
  MENSAJE_REFACTURACION_OK,
} from "shared/utils/endosoTypes";

/**
 * Endoso 25 (HU6, iteración 2): POST refacturacion sobre los recibos
 * seleccionados. Al terminar, el flujo pasa DIRECTO a la carátula del endoso
 * (la vista final de la HU): no hay pantalla intermedia de resultados.
 *
 * HU6 - reintentos (criterio de negocio): CUALQUIER error del primer intento
 * ofrece UN único reintento:
 *  - con recibos en `errores` (cualquier `codigo`): se reenvían solo esos;
 *  - error total (codigo !== 1 sin `errores`, o uuids que no cruzan con lo
 *    enviado): se reenvían todos los seleccionados.
 * Si el reintento vuelve a fallar, se cierra el modal, se avisa por toast que
 * se use el canal tradicional y el flujo termina en la carátula.
 */
export const useRefacturacion = () => {
  const { setTimer } = useContext(AuthContext);
  const {
    recibosSeleccionados,
    setRecibosSeleccionados,
    loaderRecibos,
    setLoaderRecibos,
    setShowButtons,
    createMessage,
  } = useContext(EndosoContext);

  /* Filas completas (no solo uuid) porque el body de refacturacion lleva los
   * recibos tal cual los devolvió lista-recibos. */
  const [recibosFallidos, setRecibosFallidos] = useState([]);
  const [showReintento, setShowReintento] = useState(false);

  /**
   * Fin del flujo: recién aquí se prende sendEmail, que es lo que muestra la
   * carátula/documentos. Antes se prendía al confirmar el endoso y quedaba
   * pintada detrás de los modales.
   */
  const mostrarCaratula = () => {
    setRecibosSeleccionados([]);
    setRecibosFallidos([]);
    setShowButtons((prev) => ({
      ...prev,
      refacturacion25: false,
      sendEmail: true,
    }));
  };

  /* errores trae {uuid, mensaje}; se cruzan contra las filas enviadas. */
  const filtrarFallidos = (enviados, errores = []) => {
    const uuidsError = new Set(errores.map((error) => error.uuid));
    return enviados.filter((recibo) => uuidsError.has(recibo.uuid));
  };

  const huboError = (response) =>
    response?.data?.codigo !== 1 || (response?.data?.errores ?? []).length > 0;

  const confirmarRefacturacion = async () => {
    setTimer(0);
    setLoaderRecibos(true);
    const response = await endoso.refacturacion({
      recibos: recibosSeleccionados,
    });
    setLoaderRecibos(false);

    if (!huboError(response)) {
      createMessage({ text: MENSAJE_REFACTURACION_OK, variant: "success" });
      mostrarCaratula();
      return;
    }

    /* Solo los recibos con error; si no hay cómo identificarlos (error total
     * o uuids que no cruzan), se reintenta con toda la selección. */
    const fallidos = filtrarFallidos(
      recibosSeleccionados,
      response?.data?.errores
    );
    setRecibosFallidos(fallidos.length > 0 ? fallidos : recibosSeleccionados);
    setShowReintento(true);
  };

  /* "Sí" del modal de reintento: el modal queda abierto con loader mientras
   * corre la petición. */
  const reintentarRefacturacion = async () => {
    setTimer(0);
    setLoaderRecibos(true);
    const response = await endoso.refacturacion({ recibos: recibosFallidos });
    setLoaderRecibos(false);
    setShowReintento(false);

    /* Único reintento: error total o parcial terminan igual. */
    createMessage(
      huboError(response)
        ? { text: MENSAJE_REFACTURACION_CANAL_TRADICIONAL, variant: "warning" }
        : { text: MENSAJE_REFACTURACION_OK, variant: "success" }
    );
    mostrarCaratula();
  };

  /* "No" del modal de reintento: sin reintento, directo a la carátula. */
  const rechazarReintento = () => {
    setShowReintento(false);
    mostrarCaratula();
  };

  return {
    confirmarRefacturacion,
    showReintento,
    reintentarRefacturacion,
    rechazarReintento,
    loaderRecibos,
  };
};
